import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './Sidebar.css';

const Sidebar = ({
  races,
  selectedRace,
  onRaceSelect,
  loading,
  progress,
  filters,
  onFilterChange
}) => {
  const { theme, toggleTheme } = useTheme();
  const [filtersExpanded, setFiltersExpanded] = React.useState(false);
  const [detailsExpanded, setDetailsExpanded] = React.useState(true);
  
  const handleRaceClick = (race) => {
    if (onRaceSelect) {
      onRaceSelect(race);
      setDetailsExpanded(true); // Auto-expand when selecting a race
    }
  };
  
  const clearSelection = () => {
    if (onRaceSelect) {
      onRaceSelect(null);
    }
  };

  // Get unique years from races
  const getYears = () => {
    const years = new Set();
    races.forEach(race => {
      if (race.date) {
        const year = race.date.split('/')[2];
        if (year) years.add(year);
      }
    });
    return Array.from(years).sort();
  };

  const formatDistance = (distance) => {
    if (!distance) return '';
    return `${distance} miles`;
  };

  const formatElevation = (elevation) => {
    if (!elevation) return '';
    return `${elevation}m`;
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-header-content">
          <h1>Race Map</h1>
          <p className="race-count">{races.length} races</p>
        </div>
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? '\u263D' : '\u2600'}
        </button>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div className="loading-section">
          <div className="loading-spinner"></div>
          <p>Loading routes...</p>
          {progress && (
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress.percentage}%` }}
              ></div>
              <span className="progress-text">
                {progress.current} / {progress.total}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div 
          className="section-header clickable"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <h3>Filters</h3>
          <span className="toggle-icon">{filtersExpanded ? '▼' : '▶'}</span>
        </div>
        
        {filtersExpanded && (
          <div className="filter-content">
            <div className="filter-row">
              <label>
                <input
                  type="checkbox"
                  checked={filters?.showMarathons ?? true}
                  onChange={(e) => onFilterChange('showMarathons', e.target.checked)}
                />
                Marathons
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={filters?.showUltras ?? true}
                  onChange={(e) => onFilterChange('showUltras', e.target.checked)}
                />
                Ultras
              </label>
            </div>

            <div className="filter-row">
              <label>
                <input
                  type="checkbox"
                  checked={filters?.showRoad ?? true}
                  onChange={(e) => onFilterChange('showRoad', e.target.checked)}
                />
                Road
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={filters?.showTrail ?? true}
                  onChange={(e) => onFilterChange('showTrail', e.target.checked)}
                />
                Trail
              </label>
            </div>

            <div className="filter-group">
              <label htmlFor="year-filter">Year:</label>
              <select 
                id="year-filter"
                value={filters?.year || 'all'}
                onChange={(e) => onFilterChange('year', e.target.value)}
              >
                <option value="all">All Years</option>
                {getYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Selected race details */}
      {selectedRace && (
        <div className="race-details">
          <div 
            className="section-header clickable"
            onClick={() => setDetailsExpanded(!detailsExpanded)}
          >
            <h3>Selected Race</h3>
            <div className="header-buttons">
              <button 
                className="close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                }}
                title="Clear selection"
              >
                ✕
              </button>
              <span className="toggle-icon">{detailsExpanded ? '▼' : '▶'}</span>
            </div>
          </div>
          
          {detailsExpanded && (
            <div className="race-info">
              <h4>{selectedRace.name}</h4>
              <p className="race-date">{selectedRace.date}</p>
              <div className="race-stats">
                <div className="stat">
                  <span className="stat-label">Distance:</span>
                  <span className="stat-value">{formatDistance(selectedRace.distance)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Time:</span>
                  <span className="stat-value">{selectedRace.time}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Elevation:</span>
                  <span className="stat-value">{formatElevation(selectedRace.elevation)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Position:</span>
                  <span className="stat-value">{selectedRace.position}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Type:</span>
                  <span className="stat-value">{selectedRace.type} • {selectedRace.terrain}</span>
                </div>
              </div>
              
              {/* Links */}
              <div className="race-links">
                {selectedRace.strava && (
                  <a href={selectedRace.strava} target="_blank" rel="noopener noreferrer">
                    View on Strava
                  </a>
                )}
                {selectedRace.video && (
                  <a href={selectedRace.video} target="_blank" rel="noopener noreferrer">
                    Watch Video
                  </a>
                )}
                {selectedRace.report && (
                  <a href={selectedRace.report} target="_blank" rel="noopener noreferrer">
                    Race Report
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Race list */}
      <div className="race-list">
        <h3>All Races</h3>
        <div className="race-list-items">
          {races.map(race => (
            <div
              key={race.number}
              className={`race-item ${selectedRace?.number === race.number ? 'selected' : ''}`}
              onClick={() => handleRaceClick(race)}
            >
              <div className="race-item-header">
                <span className="race-name">{race.name}</span>
                <span className={`race-badge ${(race.type || 'unknown').toLowerCase()}`}>
                  {race.type || 'Unknown'}
                </span>
              </div>
              <div className="race-item-details">
                <span>{race.date}</span>
                <span>•</span>
                <span>{formatDistance(race.distance)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
